import { fetchUtils } from 'react-admin';
import { Ticket, TicketReply, User, UserProfileForm } from '@/types';
import type {
    Card,
    CardAssignee,
    CardComment,
    CardFieldStepOption,
    CardMineSummary,
    CardProjectOption,
    CardStatus,
    Lead,
    LeadLog,
    LeadNextStep,
    LeadNextStepStatus,
} from '@/types';
import { parseError as parseCardError } from '@/modules/cards/cardApi';
import {
    parseError as parseLeadError,
    type LeadParseDraft,
    type LeadPropertyAddress,
} from '@/modules/leads/leadApi';
import { buildLeadParseForm } from '@/modules/leads/leadIntakeFiles';
import { playbackMime } from '@/modules/leads/leadOriginAudioUtils';
import { leadSourceMimeFromPath } from '@/modules/leads/leadSourceUtils';
import {
    parsePermitError,
    type PermitFileSyncResult,
    type PermitPackage,
} from '@/modules/projects/projectPermitsTypes';
import type { GlobalSearchItem } from '@/modules/global_search/globalSearchApi';
import type { SiteConfigOption } from '@/modules/site_config/SiteConfigPage';
import type { AppNotification } from '@/modules/notifications/notificationApi';
import {
    parseError as parseHelpError,
    type HelpChatResult,
    type HelpDocument,
    type HelpMessage,
} from '@/modules/help/helpApi';
import {
    type GetListParams,
    type GetOneParams,
    Identifier,
} from 'react-admin';
import {
    MapSharePointFilesResult,
    QuoteDriveItem,
    QuoteDriveList,
    QuoteDriveTree,
    parseUploadError,
} from '@/utils/quote_attachment_utils';

// @ts-ignore
import simpleRestProvider from 'ra-data-simple-rest';

export function getAccessToken() {
    const token_str = localStorage.getItem(import.meta.env.VITE_USER_STORAGE_KEY);

    if (!token_str) return null;

    const { access_token } = JSON.parse(token_str);    

    return access_token;
}

export const httpClient = async (
    url: string,
    options: fetchUtils.Options = {},
) => {
    const headers = new Headers(options.headers);
    if (!headers.has('Accept')) headers.set('Accept', 'application/json');

    const access_token = getAccessToken();

    if (!access_token) {
        return Promise.reject();
    }

    headers.set('Authorization', `Bearer ${access_token}`);

    try {
        const response = await fetchUtils.fetchJson(url, { ...options, headers });
        return response;
    } catch (error: any) {
        console.log(error);
        if (error.status === 401) {
            localStorage.removeItem(import.meta.env.VITE_USER_STORAGE_KEY);
            window.location.href = '/#/login';
        }
        return Promise.reject(error);
    }
};

const apiUrl = import.meta.env.VITE_APP_API_BACKEND as string;

const restProvider = simpleRestProvider(apiUrl, httpClient);

function includesQuery(includes: unknown): string | null {
    if (includes == null || includes === '') return null;
    const value = Array.isArray(includes)
        ? includes.join(',')
        : String(includes);
    return value ? `includes=${encodeURIComponent(value)}` : null;
}

function withIncludes(url: string, includes: unknown): string {
    const qs = includesQuery(includes);
    if (!qs) return url;
    return `${url}${url.includes('?') ? '&' : '?'}${qs}`;
}

export const dataProvider = {
    ...restProvider,
    getOne: async (resource: string, params: GetOneParams) => {
        const includes = params.meta?.includes;
        if (!includes) {
            return restProvider.getOne(resource, params);
        }
        const url = withIncludes(`${apiUrl}/${resource}/${params.id}`, includes);
        const { json } = await httpClient(url);
        return { data: json };
    },
    getList: async (resource: string, params: GetListParams) => {
        const apiResource =
            resource === 'expense_transactions'
                ? 'receipts/transactions/list'
                : resource;
        const includes = params.meta?.includes;
        if (!includes) {
            const result = await restProvider.getList(apiResource, params);
            const page = params.pagination?.page ?? 1;
            const perPage = params.pagination?.perPage ?? 10;
            // If the API returns rows for this page but Content-Range total is
            // missing/too low, react-admin treats the page as out of range and
            // resets to page 1.
            if (Array.isArray(result.data) && result.data.length > 0) {
                const minTotal = (page - 1) * perPage + result.data.length;
                if (
                    result.total == null ||
                    Number.isNaN(result.total) ||
                    result.total < minTotal
                ) {
                    return { ...result, total: minTotal };
                }
            }
            return result;
        }

        // Forward meta.includes as a top-level query param (not inside filter JSON).
        const { page = 1, perPage = 10 } = params.pagination || {};
        const { field, order } = params.sort || { field: 'id', order: 'DESC' };
        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
            filter: JSON.stringify(params.filter || {}),
        };
        const qs = Object.entries(query)
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
            .join('&');
        const url = withIncludes(`${apiUrl}/${apiResource}?${qs}`, includes);
        const { headers, json } = await httpClient(url);
        const contentRange = headers.get('content-range');
        let total = Array.isArray(json) ? json.length : 0;
        if (contentRange) {
            const match = contentRange.match(/\/(\d+|\*)/);
            if (match && match[1] !== '*') {
                total = parseInt(match[1], 10);
            }
        }
        const minTotal = (page - 1) * perPage + (Array.isArray(json) ? json.length : 0);
        if (Array.isArray(json) && json.length > 0 && total < minTotal) {
            total = minTotal;
        }
        return { data: json, total };
    },
};

// Shared authed transport for non-JSON responses (blobs, FormData uploads).
// `path` is relative to VITE_APP_API_BACKEND. Adds the Bearer header and the
// 401 redirect, then returns the raw Response for the caller to parse.
export async function authedFetch(
    path: string,
    init: RequestInit = {},
): Promise<Response> {
    const token = getAccessToken();
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (!headers.has('Accept')) headers.set('Accept', 'application/json');
    const res = await fetch(`${apiUrl}${path}`, { ...init, headers });
    if (res.status === 401) {
        localStorage.removeItem(import.meta.env.VITE_USER_STORAGE_KEY);
        window.location.href = '/#/login';
    }
    return res;
}

// Authed JSON call that surfaces a feature-specific error message on failure.
// Returns undefined for empty/204 responses (void endpoints).
async function authedJson<T>(
    path: string,
    parseErr: (data: unknown, fallback: string) => string,
    init?: RequestInit,
    fallback?: string,
): Promise<T> {
    const res = await authedFetch(path, init);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(parseErr(data, fallback ?? `HTTP ${res.status}`));
    }
    if (res.status === 204) return undefined as T;
    return res.json().catch(() => undefined as T);
}

async function driveRequest(
    path: string,
    init: RequestInit = {},
): Promise<Response> {
    const res = await authedFetch(path, init);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = parseUploadError(data, 'Request failed');
        if (
            message === 'OneDrive request failed.' ||
            message === 'Request failed'
        ) {
            console.error('OneDrive drive request failed:', {
                path,
                status: res.status,
                body: data,
            });
        }
        throw new Error(message);
    }
    return res;
}

export const dataProviderWithCustomMethod = {
    ...dataProvider,
    getQuoteDrive: async (quoteId: number, path = ''): Promise<QuoteDriveList> => {
        const qs = path ? `?path=${encodeURIComponent(path)}` : '';
        const res = await driveRequest(`/quotes/${quoteId}/drive${qs}`);
        return res.json();
    },
    getQuoteDriveTree: async (quoteId: number): Promise<QuoteDriveTree> => {
        const res = await driveRequest(`/quotes/${quoteId}/drive/tree`);
        return res.json();
    },
    uploadQuoteDriveFile: async (
        quoteId: number,
        path: string,
        file: File,
        options?: { overwrite?: boolean },
    ): Promise<QuoteDriveItem> => {
        const form = new FormData();
        form.append('path', path);
        form.append('overwrite', options?.overwrite ? 'true' : 'false');
        form.append('file', file);
        const res = await driveRequest(`/quotes/${quoteId}/drive/upload`, {
            method: 'POST',
            body: form,
        });
        return res.json();
    },
    createQuoteDriveFolder: async (
        quoteId: number,
        parentPath: string,
        name: string,
    ): Promise<QuoteDriveItem> => {
        const res = await driveRequest(`/quotes/${quoteId}/drive/folders`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ parent_path: parentPath, name }),
        });
        return res.json();
    },
    renameQuoteDriveItem: async (
        quoteId: number,
        path: string,
        newName: string,
    ): Promise<QuoteDriveItem> => {
        const res = await driveRequest(`/quotes/${quoteId}/drive/rename`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path, new_name: newName }),
        });
        return res.json();
    },
    moveQuoteDriveItem: async (
        quoteId: number,
        path: string,
        destFolder: string,
    ): Promise<QuoteDriveItem> => {
        const res = await driveRequest(`/quotes/${quoteId}/drive/move`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path, dest_folder: destFolder }),
        });
        return res.json();
    },
    deleteQuoteDriveItem: async (quoteId: number, path: string): Promise<void> => {
        await driveRequest(
            `/quotes/${quoteId}/drive?path=${encodeURIComponent(path)}`,
            { method: 'DELETE' },
        );
    },
    downloadQuoteDriveZip: async (quoteId: number, paths: string[]): Promise<Blob> => {
        const res = await driveRequest(`/quotes/${quoteId}/drive/download`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ paths }),
        });
        return res.blob();
    },
    mapQuoteSharePointFiles: async (
        quoteId: number,
    ): Promise<MapSharePointFilesResult> => {
        const res = await driveRequest(`/quotes/${quoteId}/map-sharepoint-files`, {
            method: 'POST',
        });
        return res.json();
    },
    createQuoteDriveShareLink: async (
        quoteId: number,
        path: string,
    ): Promise<{ share_link: string }> => {
        const res = await driveRequest(`/quotes/${quoteId}/drive/share`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path }),
        });
        return res.json();
    },
    updateUser: async (
        id: Identifier,
        data: UserProfileForm & { password?: string; new_password?: string; },
        editPasswordMode: boolean = false
    ): Promise<UserProfileForm> => {
        const { data: previousData } = await dataProvider.getOne(
            'users',
            {
                id,
            }
        ) as { data: User };

        if (!previousData) {
            throw new Error('User not found');
        }

        if (editPasswordMode) {
            data.password = data.new_password;
            delete data.new_password;
        } else {
            delete data.password;
            delete data.new_password;
        }

        const { data: user } = await dataProvider.update('users', {
            id,
            data,
            previousData,
        });
        return user;
    },

    getMe: async (): Promise<User> => {
        const { json } = await httpClient(`${apiUrl}/users/me`);
        return json;
    },

    updateMe: async (
        data: UserProfileForm & { password?: string; new_password?: string; confirm_password?: string },
        editPasswordMode: boolean = false
    ): Promise<User> => {
        const payload = { ...data };
        if (editPasswordMode) {
            payload.password = payload.new_password;
            delete payload.new_password;
        } else {
            delete payload.password;
            delete payload.new_password;
        }
        delete payload.confirm_password;
        const { json } = await httpClient(`${apiUrl}/users/me`, {
            method: 'PUT',
            headers: new Headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload),
        });
        return json;
    },

    getGlobalSearch: async (q: string): Promise<GlobalSearchItem[]> => {
        const { json } = await httpClient(
            `${apiUrl}/projects/global-search?q=${encodeURIComponent(q)}`,
        );
        return json;
    },

    getMetrics: async <T = unknown>(
        path: string,
        params: Record<string, string> = {},
    ): Promise<T> => {
        const qs = new URLSearchParams(params).toString();
        const { json } = await httpClient(
            `${apiUrl}/metrics${path}${qs ? `?${qs}` : ''}`,
        );
        return json;
    },

    getSiteConfig: async (): Promise<SiteConfigOption[]> => {
        const { json } = await httpClient(`${apiUrl}/site-config`);
        return json;
    },

    saveSiteConfig: async (
        values: Record<string, unknown>,
    ): Promise<SiteConfigOption[]> => {
        const { json } = await httpClient(`${apiUrl}/site-config`, {
            method: 'PUT',
            headers: new Headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ values }),
        });
        return json;
    },

    getNotifications: async (
        unreadOnly = false,
    ): Promise<AppNotification[]> => {
        const params = new URLSearchParams({ limit: '8' });
        if (unreadOnly) params.set('unread_only', 'true');
        const { json } = await httpClient(
            `${apiUrl}/notifications/?${params}`,
        );
        return json;
    },

    getUnreadNotificationCount: async (): Promise<number> => {
        const { json } = await httpClient(
            `${apiUrl}/notifications/unread-count`,
        );
        return json.count ?? 0;
    },

    markNotificationRead: async (id: number): Promise<AppNotification> => {
        const { json } = await httpClient(
            `${apiUrl}/notifications/${id}/read`,
            { method: 'PATCH' },
        );
        return json;
    },

    markAllNotificationsRead: async (): Promise<void> => {
        await httpClient(`${apiUrl}/notifications/read-all`, { method: 'POST' });
    },

    softDeleteNotification: async (id: number): Promise<void> => {
        await httpClient(`${apiUrl}/notifications/${id}`, { method: 'DELETE' });
    },

    doneNotification: async (id: number): Promise<void> => {
        await httpClient(`${apiUrl}/notifications/${id}/done`, {
            method: 'POST',
        });
    },

    snoozeNotification: async (
        id: number,
        notifyAt: string,
    ): Promise<void> => {
        await httpClient(`${apiUrl}/notifications/${id}/snooze`, {
            method: 'POST',
            headers: new Headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ notify_at: notifyAt }),
        });
    },

    getHelpMessages: async (): Promise<HelpMessage[]> => {
        const res = await authedFetch(
            '/help/messages?sort=["id","ASC"]&range=[0,199]',
        );
        const data = await res.json().catch(() => []);
        if (!res.ok) {
            throw new Error(parseHelpError(data, 'Failed to load help messages'));
        }
        return data as HelpMessage[];
    },

    getMyHelpTickets: async (): Promise<Ticket[]> => {
        const res = await authedFetch(
            '/help/tickets?sort=["id","DESC"]&range=[0,49]&includes=ticket_replies',
        );
        const data = await res.json().catch(() => []);
        if (!res.ok) {
            throw new Error(parseHelpError(data, 'Failed to load tickets'));
        }
        return data as Ticket[];
    },

    sendHelpChat: async (params: {
        messages: string[];
        files?: File[];
        screenshot?: Blob | null;
    }): Promise<HelpChatResult> => {
        const form = new FormData();
        form.append('messages', JSON.stringify(params.messages));
        for (const file of params.files || []) {
            form.append('files', file);
        }
        if (params.screenshot) {
            form.append('screenshot', params.screenshot, 'screenshot.jpg');
        }
        const res = await authedFetch('/help/chat', {
            method: 'POST',
            body: form,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(parseHelpError(data, 'Help agent request failed'));
        }
        return data as HelpChatResult;
    },

    createHelpTicket: async (params: {
        content: string;
        messageId?: number | null;
    }): Promise<Ticket> => {
        const res = await authedFetch('/help/tickets', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                content: params.content,
                message_id: params.messageId ?? null,
            }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(parseHelpError(data, 'Failed to create the ticket'));
        }
        return data as Ticket;
    },

    clearHelpMessages: async (): Promise<void> => {
        const res = await authedFetch('/help/messages', { method: 'DELETE' });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(parseHelpError(data, 'Failed to clear the chat'));
        }
    },

    createHelpDocument: async (params: {
        title: string;
        kind?: string;
        content?: string;
        is_active?: boolean;
        file?: File | null;
    }): Promise<HelpDocument> => {
        const form = new FormData();
        form.append('title', params.title);
        form.append('kind', params.kind || (params.file ? 'file' : 'text'));
        form.append('content', params.content || '');
        form.append('is_active', String(params.is_active !== false));
        if (params.file) {
            form.append('file', params.file);
        }
        const res = await authedFetch('/help_documents', {
            method: 'POST',
            body: form,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(parseHelpError(data, 'Failed to create help document'));
        }
        return data as HelpDocument;
    },

    reindexHelpDocument: async (id: number): Promise<HelpDocument> => {
        const res = await authedFetch(`/help_documents/${id}/reindex`, {
            method: 'POST',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(parseHelpError(data, 'Failed to reindex document'));
        }
        return data as HelpDocument;
    },

    getCardAssignees: (): Promise<CardAssignee[]> =>
        authedJson<CardAssignee[]>('/cards/assignees', parseCardError),

    getCardProjectOptions: (q: string): Promise<CardProjectOption[]> =>
        authedJson<CardProjectOption[]>(
            `/cards/project-options?q=${encodeURIComponent(q)}`,
            parseCardError,
        ),

    getCardFieldSteps: (projectId: Identifier): Promise<CardFieldStepOption[]> =>
        authedJson<CardFieldStepOption[]>(
            `/cards/project-options/${projectId}/field-steps`,
            parseCardError,
        ),

    getMyLastCardProject: (): Promise<CardProjectOption | null> =>
        authedJson<CardProjectOption | null>('/cards/mine/last-project', parseCardError),

    getMyCardSummary: (): Promise<CardMineSummary> =>
        authedJson<CardMineSummary>('/cards/mine/summary', parseCardError),

    updateCardStatus: (
        cardId: Identifier,
        status: CardStatus,
        position?: number,
    ): Promise<Card> => {
        const body =
            position !== undefined ? { status, position } : { status };
        return authedJson<Card>(`/cards/${cardId}/status`, parseCardError, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    },

    uploadCardFiles: (cardId: Identifier, files: File[]): Promise<void> => {
        const form = new FormData();
        files.forEach(file => form.append('files', file));
        return authedJson<void>(`/cards/${cardId}/attachments`, parseCardError, {
            method: 'POST',
            body: form,
        });
    },

    deleteCardAttachment: (
        cardId: Identifier,
        attachmentId: Identifier,
    ): Promise<void> =>
        authedJson<void>(
            `/cards/${cardId}/attachments/${attachmentId}`,
            parseCardError,
            { method: 'DELETE' },
        ),

    getCardAttachmentBlob: async (
        cardId: Identifier,
        attachmentId: Identifier,
    ): Promise<{ blob: Blob; contentType: string }> => {
        const res = await authedFetch(
            `/cards/${cardId}/attachments/${attachmentId}`,
        );
        if (!res.ok) {
            throw new Error(`Failed to fetch attachment: HTTP ${res.status}`);
        }
        const contentType =
            res.headers.get('content-type') || 'application/octet-stream';
        return { blob: await res.blob(), contentType };
    },

    createCardComment: (
        cardId: Identifier,
        text: string,
        files?: File[],
    ): Promise<CardComment> => {
        const form = new FormData();
        form.append('text', text);
        (files || []).forEach(file => form.append('files', file));
        return authedJson<CardComment>(
            `/cards/${cardId}/comments`,
            parseCardError,
            { method: 'POST', body: form },
        );
    },

    updateCardComment: (
        cardId: Identifier,
        commentId: Identifier,
        text: string,
    ): Promise<CardComment> =>
        authedJson<CardComment>(
            `/cards/${cardId}/comments/${commentId}`,
            parseCardError,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text }),
            },
        ),

    deleteCardComment: (
        cardId: Identifier,
        commentId: Identifier,
    ): Promise<void> =>
        authedJson<void>(
            `/cards/${cardId}/comments/${commentId}`,
            parseCardError,
            { method: 'DELETE' },
        ),

    createTicketReply: async (
        ticketId: Identifier,
        content: string,
    ): Promise<TicketReply> => {
        const { json } = await httpClient(
            `${apiUrl}/tickets/${ticketId}/replies`,
            {
                method: 'POST',
                headers: new Headers({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ content }),
            },
        );
        return json;
    },

    parseLeadIntake: (params: {
        text?: string;
        files?: File[];
        file?: File | null;
    }): Promise<LeadParseDraft> => {
        const files = [...(params.files || [])];
        if (params.file) files.push(params.file);
        return authedJson<LeadParseDraft>(
            '/leads/parse',
            parseLeadError,
            { method: 'POST', body: buildLeadParseForm({ text: params.text, files }) },
            'Request failed',
        );
    },

    patchLeadNextStepStatus: (
        leadId: Identifier,
        stepId: Identifier,
        status: LeadNextStepStatus,
    ): Promise<LeadNextStep> =>
        authedJson<LeadNextStep>(
            `/leads/${leadId}/next-steps/${stepId}`,
            parseLeadError,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status }),
            },
            'Request failed',
        ),

    getLeadLogs: (leadId: Identifier): Promise<LeadLog[]> =>
        authedJson<LeadLog[]>(
            `/leads/${leadId}/logs`,
            parseLeadError,
            undefined,
            'Request failed',
        ),

    createLeadLog: (leadId: Identifier, text: string): Promise<LeadLog> =>
        authedJson<LeadLog>(
            `/leads/${leadId}/logs`,
            parseLeadError,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text }),
            },
            'Request failed',
        ),

    updateLeadLog: (
        leadId: Identifier,
        logId: Identifier,
        text: string,
    ): Promise<LeadLog> =>
        authedJson<LeadLog>(
            `/leads/${leadId}/logs/${logId}`,
            parseLeadError,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text }),
            },
            'Request failed',
        ),

    deleteLeadLog: (leadId: Identifier, logId: Identifier): Promise<void> =>
        authedJson<void>(
            `/leads/${leadId}/logs/${logId}`,
            parseLeadError,
            { method: 'DELETE' },
            'Request failed',
        ),

    getLeadSource: async (
        leadId: Identifier,
        path?: string,
    ): Promise<{ blob: Blob; contentType: string }> => {
        const qs = path ? `?path=${encodeURIComponent(path)}` : '';
        const res = await authedFetch(`/leads/${leadId}/source${qs}`);
        if (!res.ok) throw new Error('Failed to load origin file');
        const blob = await res.blob();
        const headerType = (res.headers.get('content-type') || blob.type || '')
            .split(';')[0]
            .trim();
        let contentType = playbackMime(headerType) || headerType || blob.type;
        if (
            path &&
            (!contentType || contentType === 'application/octet-stream')
        ) {
            const fromPath = leadSourceMimeFromPath(path);
            if (fromPath) contentType = fromPath;
        }
        contentType = playbackMime(contentType) || contentType;
        const buf = await blob.arrayBuffer();
        return { blob: new Blob([buf], { type: contentType }), contentType };
    },

    searchLeads: (q: string): Promise<Lead[]> => {
        const params = new URLSearchParams();
        if (q.trim()) params.set('filter', JSON.stringify({ q: q.trim() }));
        params.set('range', JSON.stringify([0, 24]));
        params.set('sort', JSON.stringify(['name', 'ASC']));
        return authedJson<Lead[]>(
            `/leads?${params.toString()}`,
            parseLeadError,
            undefined,
            'Request failed',
        );
    },

    getLead: (leadId: number): Promise<Lead> =>
        authedJson<Lead>(
            `/leads/${leadId}`,
            parseLeadError,
            undefined,
            'Request failed',
        ),

    getLeadPropertyAddress: (leadId: number): Promise<LeadPropertyAddress> =>
        authedJson<LeadPropertyAddress>(
            `/leads/${leadId}/property-address`,
            parseLeadError,
            undefined,
            'Request failed',
        ),

    getProjectPermits: (projectId: number | string): Promise<PermitPackage> =>
        authedJson<PermitPackage>(
            withIncludes(`/projects/${projectId}/permits`, 'permit_sections'),
            parsePermitError,
            undefined,
            'Could not load permit documents.',
        ),

    getMunicipalityPermits: (municipalityId: number | string): Promise<PermitPackage> =>
        authedJson<PermitPackage>(
            withIncludes(`/municipalities/${municipalityId}/permits`, 'permit_sections'),
            parsePermitError,
            undefined,
            'Could not load permit documents.',
        ),

    setProjectPermitMunicipality: (
        projectId: number | string,
        municipalityKey: string,
    ): Promise<PermitPackage> =>
        authedJson<PermitPackage>(
            `/projects/${projectId}/permits/municipality`,
            parsePermitError,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ municipality_key: municipalityKey }),
            },
            'Could not save municipality.',
        ),

    uploadPermitFile: (permitFileId: number, file: File): Promise<void> => {
        const body = new FormData();
        body.append('file', file);
        return authedJson<void>(
            `/permit-files/${permitFileId}/upload`,
            parsePermitError,
            { method: 'POST', body },
            'Could not upload the permit file.',
        );
    },

    previewProjectPermit: async (
        projectId: number | string,
        docKey: string,
    ): Promise<{ blob: Blob; contentType: string }> => {
        const res = await authedFetch(
            `/projects/${projectId}/permits/${docKey}/preview`,
        );
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(parsePermitError(body, 'Could not load preview.'));
        }
        const contentType = (res.headers.get('content-type') || '')
            .split(';')[0]
            .trim()
            .toLowerCase();
        return { blob: await res.blob(), contentType };
    },

    previewPermitFile: async (
        permitFileId: number | string,
    ): Promise<{ blob: Blob; contentType: string }> => {
        const res = await authedFetch(
            `/permit-files/${permitFileId}/preview`,
        );
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(parsePermitError(body, 'Could not load preview.'));
        }
        const contentType = (res.headers.get('content-type') || '')
            .split(';')[0]
            .trim()
            .toLowerCase();
        return { blob: await res.blob(), contentType };
    },

    downloadProjectPermitToDrive: (
        projectId: number | string,
        docKey: string,
    ): Promise<void> =>
        authedJson<void>(
            `/projects/${projectId}/permits/${docKey}/download-to-drive`,
            parsePermitError,
            { method: 'POST' },
            'Could not save to Drive.',
        ),

    downloadAllProjectPermitsToDrive: (
        projectId: number | string,
    ): Promise<{ documents?: unknown[] }> =>
        authedJson<{ documents?: unknown[] }>(
            `/projects/${projectId}/permits/download-all-to-drive`,
            parsePermitError,
            { method: 'POST' },
            'Could not save all documents to Drive.',
        ),

    syncMunicipalityPermitFiles: (
        municipalityId: number,
    ): Promise<PermitFileSyncResult> =>
        authedJson<PermitFileSyncResult>(
            `/municipalities/${municipalityId}/sync-permit-files`,
            parsePermitError,
            { method: 'POST' },
            'Could not sync permit documents.',
        ),
};

export type CrmDataProvider = typeof dataProviderWithCustomMethod;
